const METRIC_NAMES = [
  'totalRevenue',
  'totalDeviceRevenue',
  'stewardPayable',
  'kudPayable',
  'puskudPayable'
];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const buildUnavailable = (reason) => ({
  available: false,
  balanced: false,
  reason,
  metrics: {}
});

export const buildProfitSharingAccumulationView = (dashboard = {}) => {
  const period = dashboard?.period?.accumulation;
  if (!period || !['year_to_date', 'full_year'].includes(period.kind)) {
    return {
      kind: 'unavailable',
      isYtd: false,
      year: null,
      period: null,
      totals: {},
      reconciliation: buildUnavailable('missing_period')
    };
  }

  const isYtd = period.kind === 'year_to_date';
  const totals = isYtd ? dashboard.ytd : dashboard.annual;
  if (!totals || typeof totals !== 'object') {
    return {
      kind: period.kind,
      isYtd,
      year: Number(period.year),
      period,
      totals: {},
      reconciliation: buildUnavailable('missing_totals')
    };
  }

  const unavailableMetrics = METRIC_NAMES.filter((name) => (
    totals[name] === null || totals[name] === undefined
  ));
  if (unavailableMetrics.length > 0) {
    return {
      kind: period.kind,
      isYtd,
      year: Number(period.year),
      period,
      totals,
      reconciliation: {
        ...buildUnavailable('unavailable_metrics'),
        unavailableMetrics
      }
    };
  }

  const months = Array.isArray(dashboard.accumulationMonths) ? dashboard.accumulationMonths : [];
  const throughMonth = Number(period.throughMonth);
  const expectedMonths = Number.isInteger(throughMonth) && throughMonth >= 1 && throughMonth <= 12
    ? Array.from({ length: throughMonth }, (_, index) => index + 1)
    : [];
  const receivedMonths = [...new Set(months.map((row) => Number(row.month)).filter(Number.isInteger))].sort((a, b) => a - b);
  const complete = expectedMonths.length > 0
    && expectedMonths.length === receivedMonths.length
    && expectedMonths.every((month, index) => month === receivedMonths[index]);

  if (!complete) {
    return {
      kind: period.kind,
      isYtd,
      year: Number(period.year),
      period,
      totals,
      reconciliation: buildUnavailable('incomplete_months')
    };
  }

  const metrics = Object.fromEntries(METRIC_NAMES.map((name) => {
    const total = toNumber(totals[name]);
    const monthlySum = months.reduce((sum, row) => sum + toNumber(row[name]), 0);
    const difference = Number((total - monthlySum).toFixed(2));
    return [name, { total, monthlySum, difference, balanced: Math.abs(difference) < 0.005 }];
  }));
  const locallyBalanced = Object.values(metrics).every((metric) => metric.balanced);
  const serverBalanced = dashboard?.accumulationReconciliation?.balanced !== false;
  const balanced = locallyBalanced && serverBalanced;

  return {
    kind: period.kind,
    isYtd,
    year: Number(period.year),
    period,
    totals,
    reconciliation: {
      available: true,
      balanced,
      reason: !locallyBalanced ? 'local_mismatch' : !serverBalanced ? 'server_mismatch' : null,
      metrics
    }
  };
};

export { METRIC_NAMES };
